import React, { useEffect, useState } from "react";
import Button from "../../../components/ui/Button";
import { AlertCircle, Clock } from "lucide-react";
import { useSelector } from "react-redux";
import { useAppDispatch } from "../../../redux";
import CategorySelection from "../components/ProfessionalProfileComponents/CategorySelection";
import ServiceAreaSelection from "../components/ProfessionalProfileComponents/ServiceAreaSelection";
import { saveUserCategories, getUserCategoriesByType } from "../../../redux/Actions/UserCategoryActions";
import { UserCategoryTypeEnum } from "../../../redux/Slices/userCategorySlice";
import { ServiceArea } from "../../admin/models/ServiceArea.model";
import { UpdateUserProfile } from "../../../redux/Actions/AuthAction";
import { GlobalService } from "../../shared";
import { deleteUserAvailabilities, getUserAvailabilities } from "../../../redux/Actions/ProfessionalActions";
import { useTranslation } from "react-i18next";
import TimeInput from "../../../components/ui/TimeInput";
interface DayHours {
    start: string;
    end: string;
    active: boolean;
}

interface RegularHours {
    [key: string]: DayHours;
}

interface Category {
    id: string;
    name: string;
    description: string;
    subcategories: string[];
    isEmergency?: boolean;
    isRequireVerification?: boolean;
    type?: { id: string | number };
}

interface Subcategory {
    id: string;
    name: string;
    description?: string;
}

interface AvailabilityModalProps {
    open: boolean;
    regularHours: RegularHours;
    isEmergency: boolean;
    handleSave: (updatedRegularHours: RegularHours) => void;
    onClose: () => void;
    onEmergencyHoursSaved?: () => void; // Callback to reset regular hours when emergency hours are saved
    onEmergencyDataReset?: () => void; // Callback when emergency data is reset
    resetData?: boolean; // Whether to show reset checkbox
    onResetDataChange?: (reset: boolean) => void; // Callback for reset checkbox change
}

const AvailabilityModal = ({ isEmergency, open, regularHours, handleSave, onClose, onEmergencyHoursSaved, onEmergencyDataReset, resetData, onResetDataChange }: AvailabilityModalProps) => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const { user } = useSelector((state: any) => state.auth);

    const [localRegularHours, setLocalRegularHours] = useState<RegularHours>({});
    const [whatsappNumber, setWhatsappNumber] = useState(user?.whatsappNumber || user?.contactOnWhatsapp || "");

    // Time format preference (12-hour or 24-hour)
    const [use24HourFormat, setUse24HourFormat] = useState<boolean>(() => {
        const saved = localStorage.getItem("timeFormat");
        return saved ? saved === "24" : false; // Default to 12-hour (false)
    });

    // Category and service area selection state for emergency availability
    const [selectedCategories, setSelectedCategories] = useState<Category[]>([]);
    const [selectedSubcategories, setSelectedSubcategories] = useState<Subcategory[]>([]);
    const [selectedServiceAreas, setSelectedServiceAreas] = useState<ServiceArea[]>([]);
    const [categoryType, setCategoryType] = useState<number>(1); // Default to Home Service
    const [resetEmergencyData, setResetEmergencyData] = useState(false);
    const { userCategories } = useSelector((state: any) => state.userCategory);
    const categoriesByType = useSelector((state: any) => state.client.categoriesByType);
    const { emergencyAvailabilities } = useSelector((state: any) => state.professional);

    const days = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

    // Save time format preference to localStorage
    const handleTimeFormatChange = (format: "12" | "24") => {
        const is24Hour = format === "24";
        setUse24HourFormat(is24Hour);
        localStorage.setItem("timeFormat", format);
    };

    useEffect(() => {
        setLocalRegularHours(regularHours);
    }, [regularHours]);

    // Fetch emergency categories and service areas when modal opens with isEmergency=true
    useEffect(() => {
        if (open && isEmergency && user?.id) {
            fetchEmergencyCategories();
        }
    }, [open, isEmergency, user?.id]);

    // Debug: Log selectedCategories changes
    useEffect(() => {
        if (isEmergency) {
            console.log("Emergency modal - selectedCategories changed:", selectedCategories);
            console.log("Emergency modal - selectedSubcategories changed:", selectedSubcategories);
            console.log("Emergency modal - selectedServiceAreas changed:", selectedServiceAreas);
        }
    }, [selectedCategories, selectedSubcategories, selectedServiceAreas, isEmergency]);

    // Update WhatsApp number when user data changes
    useEffect(() => {
        if (user?.whatsappNumber || user?.contactOnWhatsapp) {
            setWhatsappNumber(user.whatsappNumber || user.contactOnWhatsapp || "");
        }
    }, [user?.whatsappNumber, user?.contactOnWhatsapp]);

    const fetchEmergencyCategories = async () => {
        if (!user?.id) return;

        console.log("=== Emergency Modal - Fetching Categories ===");
        console.log("User ID:", user.id);
        console.log("Category Type:", categoryType);
        console.log("===========================================");

        try {
            // Check if we already have the data in Redux state
            const cacheKey = `${user.id}-${UserCategoryTypeEnum.Emergency}`;
            const cachedData = userCategories[cacheKey];

            if (cachedData) {
                // Use cached data
                const categories = cachedData.categoryIds || [];
                const subCategories = cachedData.subCategories || [];
                const serviceAreas = cachedData.serviceAreaIds || [];

                console.log("Using cached emergency data:", { categories, subCategories, serviceAreas });
                console.log("Cached service area details:", serviceAreas.map(area => ({
                    id: area.id,
                    type: area.type,
                    country: area.country
                })));

                setSelectedCategories(categories.map(cat => ({
                    id: cat.id, // Keep as string, don't parse
                    name: cat.name,
                    description: cat.description || "",
                    subcategories: cat.subcategories || [],
                    isEmergency: cat.isEmergency,
                    isRequireVerification: cat.isRequireVerification,
                    type: cat.type
                })));
                setSelectedSubcategories(subCategories.map((name) => ({
                    id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                    name,
                    description: ""
                })));
                setSelectedServiceAreas(serviceAreas.map((area: any) => ({
                    id: String(area.id), // Ensure ID is always a string
                    name: area.name || "",
                    province: area.province || "",
                    country: {
                        id: String(area.country?.id || ""), // Ensure country ID is string
                        name: area.country?.name || "",
                        code: area.country?.code || area.country?.initial || ""
                    },
                    type: {
                        id: Number(area.type?.id) || 1, // Ensure type ID is number as expected by interface
                        name: area.type?.name || "Default"
                    },
                    radius: Number(area.radius) || 0,
                    restrictions: area.restrictions || [],
                    cities: area.cities || [],
                    postalCodes: area.postalCodes || [],
                    serviceTypes: area.serviceTypes || [],
                    emergencyServices: Boolean(area.emergencyServices) || false
                })));
                return;
            }

            // Fetch from backend if not cached
            const response = await dispatch(getUserCategoriesByType({
                userId: user.id,
                type: UserCategoryTypeEnum.Emergency
            })).unwrap();

            if (response) {
                const categories = response.categoryIds || [];
                const subCategories = response.subCategories || [];
                const serviceAreas = response.serviceAreaIds || [];

                console.log("Emergency categories fetched:", categories);
                console.log("Emergency subcategories fetched:", subCategories);
                console.log("Emergency service areas fetched:", serviceAreas);
                console.log("Service area details:", serviceAreas.map(area => ({
                    id: area.id,
                    type: area.type,
                    country: area.country
                })));

                setSelectedCategories(categories.map(cat => ({
                    id: cat.id, // Keep as string, don't parse
                    name: cat.name,
                    description: cat.description || "",
                    subcategories: cat.subcategories || [],
                    isEmergency: cat.isEmergency,
                    isRequireVerification: cat.isRequireVerification,
                    type: cat.type
                })));
                setSelectedSubcategories(subCategories.map((name) => ({
                    id: name, // Use subcategory name as ID since backend doesn't provide separate IDs
                    name,
                    description: ""
                })));
                setSelectedServiceAreas(serviceAreas.map((area: any) => ({
                    id: String(area.id), // Ensure ID is always a string
                    name: area.name || "",
                    province: area.province || "",
                    country: {
                        id: String(area.country?.id || ""), // Ensure country ID is string
                        name: area.country?.name || "",
                        code: area.country?.code || area.country?.initial || ""
                    },
                    type: {
                        id: Number(area.type?.id) || 1, // Ensure type ID is number as expected by interface
                        name: area.type?.name || "Default"
                    },
                    radius: Number(area.radius) || 0,
                    restrictions: area.restrictions || [],
                    cities: area.cities || [],
                    postalCodes: area.postalCodes || [],
                    serviceTypes: area.serviceTypes || [],
                    emergencyServices: Boolean(area.emergencyServices) || false
                })));
            }
        } catch (error) {
            console.error("Error fetching emergency categories:", error);
        }
    };

    const handleHourChange = (day: string, field: keyof DayHours, value: string | boolean) => {
        setLocalRegularHours(prev => {
            const currentDay: DayHours = prev[day] || { start: "", end: "", active: false };
            
            // When activating a day, ensure default times are set
            if (field === "active" && value === true) {
                return {
                    ...prev,
                    [day]: {
                        start: currentDay.start || "09:00",
                        end: currentDay.end || "17:00",
                        active: true,
                    },
                };
            }
            
            // For other changes, just update the field
            return {
                ...prev,
                [day]: { ...currentDay, [field]: value },
            };
        });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        // Validate WhatsApp number for emergency availability
        if (isEmergency && !whatsappNumber.trim()) {
            GlobalService.showToast("error", t("toast.availabilities.validationError"), t("toast.availabilities.whatsappRequired"));
            return;
        }

        // Validate categories and service areas for emergency availability (only if not resetting)
        if (isEmergency && !resetEmergencyData) {
            if (selectedCategories.length === 0) {
                GlobalService.showToast("error", t("toast.availabilities.validationError"), t("toast.availabilities.emergencyCategoryRequired"));
                return;
            }

            if (selectedServiceAreas.length === 0) {
                GlobalService.showToast("error", t("toast.availabilities.validationError"), t("toast.availabilities.emergencyAreaRequired"));
                return;
            }
        }

        // Update user profile with WhatsApp number if it has changed
        if (isEmergency && user?.id && whatsappNumber.trim()) {
            const currentWhatsApp = user?.whatsappNumber || user?.contactOnWhatsapp || "";
            if (whatsappNumber.trim() !== currentWhatsApp) {
                try {
                    await dispatch(UpdateUserProfile({
                        id: user.id,
                        data: {
                            ...user,
                            whatsappNumber: whatsappNumber.trim(),
                            contactOnWhatsapp: true
                        }
                    }));
                    console.log("WhatsApp number updated successfully");
                } catch (error) {
                    console.error("Error updating WhatsApp number:", error);
                }
            }
        }

        // Save availability hours
        handleSave(localRegularHours);

        // Save categories and service areas for emergency availability
        if (isEmergency && user?.id) {
            try {
                if (resetEmergencyData) {
                    // Reset emergency data - save empty records
                    await dispatch(saveUserCategories({
                        userId: user.id,
                        type: UserCategoryTypeEnum.Emergency, // Type 2 for emergency
                        categoryIds: [], // Empty categories
                        subCategories: [], // Empty subcategories
                        serviceAreaIds: [] // Empty service areas
                    }));

                    // Delete emergency availabilities if they exist
                    if (emergencyAvailabilities && emergencyAvailabilities.length > 0) {
                        for (const availability of emergencyAvailabilities) {
                            try {
                                await dispatch(deleteUserAvailabilities({ id: availability.id }));
                            } catch (error) {
                                console.error("Error deleting emergency availability:", error);
                            }
                        }
                    }

                    console.log("Emergency data reset successfully");
                    GlobalService.showToast("success", t("toast.titles.success"), t("toast.availabilities.emergencyDataReset"));

                    // Call callback to notify parent component after a small delay to ensure state updates
                    setTimeout(() => {
                        if (onEmergencyDataReset) {
                            onEmergencyDataReset();
                        }
                    }, 100);
                } else {


                    await dispatch(saveUserCategories({
                        userId: user.id,
                        type: UserCategoryTypeEnum.Emergency, // Type 2 for emergency
                        categoryIds: selectedCategories.length > 0 ? selectedCategories.map(cat => ({ id: cat.id })) : [],
                        subCategories: selectedSubcategories.length > 0 ? selectedSubcategories.map(sub => sub.name) : [],
                        serviceAreaIds: selectedServiceAreas.length > 0 ? selectedServiceAreas.map(area => ({ id: area.id })) : []
                    }));

                    // Save empty categories and service areas for type 1 (Profile) to ensure emergency data takes precedence
                    await dispatch(saveUserCategories({
                        userId: user.id,
                        type: UserCategoryTypeEnum.Profile, // Type 1 for profile
                        categoryIds: [], // Empty categories
                        subCategories: [], // Empty subcategories
                        serviceAreaIds: [] // Empty service areas
                    }));

                    console.log("Emergency categories and service areas saved successfully");
                    console.log("Profile categories and service areas cleared to ensure emergency data precedence");
                }

                // Call callback to reset regular hours
                if (onEmergencyHoursSaved) {
                    onEmergencyHoursSaved();
                }
            } catch (error) {
                console.error("Error saving emergency categories and service areas:", error);
            }
        }

        onClose();
    };
    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl shadow-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="max-h-[80vh] overflow-y-auto text-left">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-xl font-semibold mb-4">{t("availabilityModal.title")}</h3>
                        <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl">
                            &times;
                        </button>
                    </div>
                    <form onSubmit={handleSubmit} className="space-y-6">
                        {isEmergency && (
                            <>
                                {/* Important Notice */}
                                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                                    <div className="flex items-start">
                                        <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center mr-3 mt-0.5">
                                            <AlertCircle className="w-4 h-4 text-white" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-red-800 mb-2">{t("availabilityModal.importantNotice")}</h4>
                                            <p className="text-red-700 text-sm">
                                                {t("availabilityModal.emergencyNoticeMessage")}{" "}
                                                <span className="font-bold">{t("availabilityModal.emergencyProfessional")}</span>. {t("availabilityModal.emergencyNoticeDetails")}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                {/* WhatsApp Number Input */}
                                <div className="mb-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        {t("availabilityModal.whatsappNumber")} <span className="text-red-500">*</span>
                                    </label>
                                    <div className="relative">
                                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                            <span className="text-black-500 text-sm">+</span>
                                        </div>
                                        <input
                                            type="tel"
                                            value={whatsappNumber.replace(/^\+/, "")}
                                            onChange={(e) => {
                                                let value = e.target.value.replace(/\D/g, "");
                                                if (value) {
                                                    value = "+" + value;
                                                }
                                                setWhatsappNumber(value);
                                            }}
                                            placeholder={t("availabilityModal.whatsappPlaceholder")}
                                            className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                                            required
                                        />
                                    </div>
                                    <p className="text-gray-600 text-sm mt-1">
                                        {user?.whatsappNumber || user?.contactOnWhatsapp
                                            ? t("availabilityModal.whatsappUpdateHint")
                                            : t("availabilityModal.whatsappRequiredHint")
                                        }
                                    </p>
                                </div>
                            </>
                        )}
                        <div>
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="font-medium">{isEmergency ? t("availabilityModal.emergencyHours") : t("availabilityModal.regularHours")}</h4>
                                
                                {/* Time Format Toggle */}
                                <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
                                    <button
                                        type="button"
                                        onClick={() => handleTimeFormatChange("12")}
                                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                            !use24HourFormat
                                                ? "bg-white text-blue-600 shadow-sm"
                                                : "text-gray-600 hover:text-gray-900"
                                        }`}
                                    >
                                        12h
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => handleTimeFormatChange("24")}
                                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
                                            use24HourFormat
                                                ? "bg-white text-blue-600 shadow-sm"
                                                : "text-gray-600 hover:text-gray-900"
                                        }`}
                                    >
                                        24h
                                    </button>
                                </div>
                            </div>
                            
                            {days.map(day => (
                                <div key={day} className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 py-3 border-b last:border-b-0">
                                    <div className="flex items-center gap-3 w-full sm:w-auto">
                                        <div className="w-24 capitalize font-medium text-gray-700">{t(`availabilityModal.days.${day}`)}</div>
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={localRegularHours[day]?.active || false}
                                                onChange={e => handleHourChange(day, "active", e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-blue-600 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all" />
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full sm:w-auto sm:ml-auto">
                                        <TimeInput
                                            value={localRegularHours[day]?.start || "09:00"}
                                            onChange={(value) => handleHourChange(day, "start", value)}
                                            disabled={!localRegularHours[day]?.active}
                                            use24Hour={use24HourFormat}
                                            className="flex-shrink-0"
                                        />
                                        <span className="text-gray-500 text-sm">{t("availabilityModal.to")}</span>
                                        <TimeInput
                                            value={localRegularHours[day]?.end || "17:00"}
                                            onChange={(value) => handleHourChange(day, "end", value)}
                                            disabled={!localRegularHours[day]?.active}
                                            use24Hour={use24HourFormat}
                                            className="flex-shrink-0"
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Category Selection for Emergency Availability */}
                        {isEmergency &&
                            <>
                                <div className="mb-6">
                                    <h4 className="font-medium mb-3">
                                        {t("availabilityModal.emergencyCategories")} <span className="text-red-500">*</span>
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t("availabilityModal.categoriesHint")}
                                    </p>
                                    <CategorySelection
                                        selectedCategories={selectedCategories}
                                        selectedSubcategories={selectedSubcategories}
                                        onCategoriesChange={setSelectedCategories}
                                        onSubcategoriesChange={setSelectedSubcategories}
                                        disabled={false}
                                        professionalType={categoryType} />
                                </div>
                                <div className="mb-6">
                                    <h4 className="font-medium mb-3">
                                        {t("availabilityModal.emergencyAreas")} <span className="text-red-500">*</span>
                                    </h4>
                                    <p className="text-sm text-gray-600 mb-2">
                                        {t("availabilityModal.areasHint")}
                                    </p>
                                    <ServiceAreaSelection
                                        selectedServiceAreas={selectedServiceAreas}
                                        onServiceAreasChange={setSelectedServiceAreas}
                                        disabled={false} />
                                </div></>
                        }
                        <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-2">
                                <input
                                    type="checkbox"
                                    id="resetData"
                                    checked={isEmergency ? resetEmergencyData : resetData}
                                    onChange={(e) => {
                                        if (isEmergency) {
                                            setResetEmergencyData(e.target.checked);
                                        } else if (onResetDataChange) {
                                            onResetDataChange(e.target.checked);
                                        }
                                    }}
                                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                />
                                <label htmlFor="resetData" className="text-sm text-gray-700">
                                    {isEmergency ? t("availabilityModal.resetEmergency") : t("availabilityModal.resetRegular")}
                                </label>
                            </div>
                            <div className="flex gap-4">
                                <Button type="button" variant="outline" onClick={onClose}>
                                    {t("availabilityModal.cancel")}
                                </Button>
                                <Button type="submit">
                                    {(isEmergency ? resetEmergencyData : resetData) ? t("availabilityModal.resetAndSave") : t("availabilityModal.saveChanges")}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AvailabilityModal;
